import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import useStickyState from '@hooks/useStickyState'
import IUser from '@interfaces/IUser'
import * as $Auth from '@services/Auth'
import * as $Sanctum from '@services/Sanctum'
import { Outlet, useNavigate } from 'react-router-dom'
import * as $Donor from '@services/Donor'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import IOrder from '@interfaces/IOrder'
import * as $Order from '@services/Order'
import OrderStatusEnum from '@enums/OrderStatusEnum'

const stethoscopeSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M11 2v2"/><path d="M5 2v2"/><path d="M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1"/><path d="M8 15a6 6 0 0 0 12 0v-3"/><circle cx="20" cy="10" r="2"/></svg>'

type AuthContextProps = {
  isLoading: boolean
  isAuthenticated: boolean
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
  user: IUser|null
  setUser: React.Dispatch<React.SetStateAction<IUser|null>>
  cart: IOrder
  setCart: React.Dispatch<React.SetStateAction<IOrder>>
  cartCount: number
  setCartCount: React.Dispatch<React.SetStateAction<number>>
  inProgressOrders: IOrder[]
  activeInProgressOrders: IOrder[]
  setInProgressOrders: React.Dispatch<React.SetStateAction<IOrder[]>>
  ordersCount: number
  setOrdersCount: React.Dispatch<React.SetStateAction<number>>
  favoritesCount: number
  setFavoritesCount: React.Dispatch<React.SetStateAction<number>>
  token: string|null
  setToken: React.Dispatch<React.SetStateAction<string|null>>
  logout: () => void
  refetchUser: () => Promise<void>
  onFavoriteDonor: (donorId: string, callback: (isFavorited: boolean) => void) => Promise<void>
  onAddDonorToCart: (donorId: string, callback?: (success: boolean) => void) => Promise<void>
  onRemoveDonorFromCart: (donorId: string) => Promise<void>
  onUpdateCart: (data: any) => Promise<void>
  onRequestReservation: (orderInternalId: string | number, data: any) => Promise<number>
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps)

export const AuthProvider: React.FC = () => {
  const [ token, setToken ] = useStickyState<string|null>(null, 'token')
  const [ user, setUser ] = useState<IUser|null>(null)
  const [ favoritesCount, setFavoritesCount ] = useState<number>(0)
  const [ isAuthenticated, setIsAuthenticated ] = useState<boolean>(false)
  const [ isLoading, setIsLoading ] = useState<boolean>(token !== null && token.length > 0)

  const [ cart, setCart ] = useStickyState<IOrder>({} as IOrder, 'cart')
  const [ cartCount, setCartCount ] = useState<number>(0)

  const [ inProgressOrders, setInProgressOrders ] = useStickyState<IOrder[]>([], 'inProgressOrders')
  const activeInProgressOrders = useMemo(() => inProgressOrders.filter(o => o.status !== OrderStatusEnum.CANCELED && o.status !== OrderStatusEnum.SENDED && o.status !== OrderStatusEnum.DELIVERED && o.status !== OrderStatusEnum.AFTER_SALES), [inProgressOrders])
  const [ ordersCount, setOrdersCount ] = useState<number>(0)

  const navigate = useNavigate()

  const SweetAlert = withReactContent(Swal)

  useEffect(() => {
    $Sanctum.csrf()
  }, [])

  useEffect(() => {
    if (token?.length) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      $Auth.me().then(({ data: user }) => {
        setUser(user)
        setFavoritesCount(user.favorites_count || 0)

        if (user.patient.cart) {
          setCart(user.patient.cart as IOrder)
          setCartCount(user.patient.cart.donors_count || 0)
        } else {
          setCart({} as IOrder)
          setCartCount(0)
        }

        if (user.patient.in_progress_orders && Array.isArray(user.patient.in_progress_orders)) {
          setInProgressOrders(user.patient.in_progress_orders as IOrder[])
        } else {
          setInProgressOrders([])
        }

        const hasCart = user.patient.cart && Object.keys(user.patient.cart).length > 0 ? 1 : 0
        const inProgressOrdersCount = user.patient.in_progress_orders?.length || 0
        setOrdersCount(hasCart + inProgressOrdersCount)

        setIsAuthenticated(true)
      }).catch(() => setToken(null)).finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setToken, token])

  const logout = useCallback(() => {
    setUser(null)
    setFavoritesCount(0)
    setToken('')
    setIsAuthenticated(false)
    localStorage.removeItem('proseed_selected_order_id')
    navigate('/')
  }, [navigate, setToken])

  // ── Inactivity logout (7 days) ────────────────────────────────────────────────────────────────
  // TODO: Review the inactivity timeout
  const INACTIVITY_TIMEOUT = 7 * 24 * 60 * 60 * 1000 // 7 days in milliseconds
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    if (isAuthenticated) {
      inactivityTimer.current = setTimeout(() => {
        logout()
      }, INACTIVITY_TIMEOUT)
    }
  }, [isAuthenticated, logout])

  useEffect(() => {
    if (!isAuthenticated) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
      return
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const
    events.forEach((event) => window.addEventListener(event, resetInactivityTimer))
    resetInactivityTimer()

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetInactivityTimer))
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current)
    }
  }, [isAuthenticated, resetInactivityTimer])

  const refetchUser = useCallback(async (): Promise<void> => {
    if (!token?.length) return

    try {
      const { data: user } = await $Auth.me()
      setUser(user)
      setFavoritesCount(user.favorites_count || 0)

      if (user.patient.cart) {
        setCart(user.patient.cart as IOrder)
        setCartCount(user.patient.cart.donors_count || 0)
      } else {
        setCart({} as IOrder)
        setCartCount(0)
      }

      if (user.patient.in_progress_orders && Array.isArray(user.patient.in_progress_orders)) {
        setInProgressOrders(user.patient.in_progress_orders as IOrder[])
      } else {
        setInProgressOrders([])
      }

      const hasCart = user.patient.cart && Object.keys(user.patient.cart).length > 0 ? 1 : 0
      const inProgressOrdersCount = user.patient.in_progress_orders?.length || 0
      setOrdersCount(hasCart + inProgressOrdersCount)

      setIsAuthenticated(true)
    } catch (error) {
      console.error('Error refetching user:', error)
    }
  }, [token, setCart, setInProgressOrders])

  const onFavoriteDonor = useCallback(async (donorId: string, callback?: (isFavorited: boolean) => void): Promise<void> => {
    return $Donor.favorite(donorId).then(({ data: { is_favorited: isFavorited, favorites_count: favoritesCount }}) => {

      setFavoritesCount(favoritesCount)

      if (callback) callback(isFavorited)
    }).catch(() => {
      SweetAlert.fire({
        title: `Erro ao favoritar doador ${donorId}`,
        icon: 'error',
        text: `Ocorreu um erro ao favoritar o doador ${donorId}. Tente novamente mais tarde.`,
      })
    })
  }, [SweetAlert])

  const onAddDonorToCart = useCallback(async (donorId: string, callback?: (success: boolean) => void): Promise<void> => {
    let addData: { has_treatment_clinic?: boolean } = {}

    if (cartCount === 0) {
      if (user?.patient?.has_clinic && user?.patient?.clinic_id) {
        addData = { has_treatment_clinic: true }
      } else {
        const result = await SweetAlert.fire({
          title: 'Clínica de Tratamento',
          html: 'Para aquisição do sêmen de doador, é necessário o acompanhamento médico e envio da amostra para a clínica.<br/><br/>Você já tem uma clínica para o tratamento?',
          icon: 'question',
          iconHtml: stethoscopeSvg,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Tenho Clínica',
          denyButtonText: 'Não Tenho Clínica',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#5673b5',
          allowOutsideClick: false,
        })

        if (result.isDismissed) {
          if (callback) callback(false)
          return
        }

        addData = { has_treatment_clinic: result.isConfirmed }
      }
    }

    return $Order.addToCart(donorId, addData).then(({ data }) => {

      if (data.donors_count !== undefined) {
        setCartCount(data.donors_count)

        if (callback) callback(true)
      } else {
        SweetAlert.fire({
          title: `Doador ${donorId} já está no carrinho`,
          icon: 'info',
        })

        if (callback) callback(false)
      }
    }).catch((error) => {
      const message = error.response?.data?.message || 'Erro ao adicionar doador ao carrinho'
      SweetAlert.fire('Erro', message, 'error')

      if (callback) callback(false)
    })
  }, [SweetAlert, cartCount, user])

  const onRemoveDonorFromCart = useCallback(async (donorId: string): Promise<void> => {
    return $Order.removeFromCart(donorId).then(({ data }) => {

      if (data.donors_count !== undefined) {
        setCartCount(data.donors_count)
      } else {
        SweetAlert.fire({
          title: `Doador ${donorId} não está no carrinho`,
          icon: 'info',
        })
      }
    }).catch((error) => {
      const message = error.response?.data?.message || 'Erro ao remover doador do carrinho'
      SweetAlert.fire('Erro', message, 'error')
    })
  }, [SweetAlert])

  const onUpdateCart = useCallback(async (data: any): Promise<void> => {
    return $Order.updateCurrentCart(data).then(({ data }) => {
      setCart(data)
    }).catch((error) => {
      const message = error.response?.data?.message || 'Erro ao atualizar o carrinho'
      SweetAlert.fire('Erro', message, 'error')
    })
  }, [SweetAlert, setCart])

  const onRequestReservation = useCallback(async (orderInternalId: string | number, data: any): Promise<number> => {
    return $Order.reservationRequest(orderInternalId, data).then(({ data: order }) => {
      setCart({} as IOrder)
      setCartCount(0)
      return order.id as number
    }).catch((error) => {
      const message = error.response?.data?.message || 'Erro ao solicitar reserva'
      SweetAlert.fire('Erro', message, 'error')
      throw error
    })
  }, [SweetAlert, setCart])

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        cart,
        setCart,
        cartCount,
        setCartCount,
        inProgressOrders,
        activeInProgressOrders,
        ordersCount,
        setOrdersCount,
        setInProgressOrders,
        favoritesCount,
        setFavoritesCount,
        token,
        setToken,
        logout,
        refetchUser,
        onFavoriteDonor,
        onAddDonorToCart,
        onRemoveDonorFromCart,
        onUpdateCart,
        onRequestReservation,
      }}
    >
      <Outlet />
    </AuthContext.Provider>

  )
}

export default AuthContext
